/**
 * Definitions of all data types returned by the API.
 *
 * We sometimes commit new types ahead of the rest of the features,
 * to avoid conflicts when working on different features in parallel,
 * so we allow unused code here.  It depends on the backend anyway.
 */

export interface ICollection {
  contentCount: number;
  createDate: number;
  description: string | null;
  folderId: string;
  folderName: string;
  published: boolean;
  thumba?: string | null;
  thumbs?: string | null;
  thumb: string | null;
  userId: string;
}

export interface ICollectionSearchResults {
  collections: ICollection[];
  users: IUser[];
  page: number;
  pages: number;
  totalCount: number;
}

export interface ICurrentCollectionSearchParams {
  userId?: string;
  collectionId: string;
}

export interface IEditCollectionParams {
  published: boolean;
  description: string | null;
  folderName: string;
  collectionId: string;
}

export interface ICreateCollectionParams {
  published: boolean;
  description: string | null;
  folderName: string;
}

export interface IAddGifParamsToCollection {
  collectionId: string;
  gifId: string;
  context: string | null;
}

export interface ICreatorsItems {
  type: string;
  text: string;
  image: string | null;
  verified: boolean;
  name: string;
  userId: string;
  studio: boolean;
  followers: number;
}

export interface IExperimentList {
  [key: string]: boolean | string;
}

export interface IFollowersListResponse {
  status: string;
  followers: IUser[];
  count: number;
  cursor: string | null;
  totalCount: number;
  page: number;
  pages: number;
}

export interface IGallery {
  id: string;
  gifs: IGif[];
  users: IUserMap;
}

export interface IGif {
    width: number;
    id: string;
    avgColor: string;
    description: string | null;
    createDate: number;
    duration: number;
    gallery: string | null;
    hasAudio: boolean;
    height: number;
    hideHome: boolean;
    hideTrending: boolean;
    likes: number;
    sexuality?: string[];
    liked?: number;
    niches: string[];
    views: number;
    published: boolean;
    userName: string;
    hls: boolean;
    source?: {
        host: string;
        url: string;
    };

    tags: string[];
    type: number;
    urls: {
        hd: string;
        sd: string;
        poster: string;
        thumbnail: string;
        vthumbnail: string;
    };

    user?: IUser;
    verified: boolean;
    cta?: {
      link: string;
      text: string;
      frequency: number;
      campaignId: string;
      showReason: string;
    };
    aliasId?: string;
    repost?: {
      username: string;
      timestamp: number;
    };
    feedback?: {
      downvotes: number;
      upvotes: number;
      state: string | null;
      userName: string;
      timestamp: number;
    };
    studio?: boolean;
}

export interface IGifInfo {
  gif: IGif;
  user: IUser | null;
  niches: INiche[];
}

export interface IGifList {
  page: number | null;
  pages: number | null;
  total: number | null;
  gifs: IGif[];
  users: IUser[];
  tags: string[];
  niches: INiche[];
  prefetch?: string[];
  feed_id?: string | null;
  cursor?: string | null;
  boosted_gifs?: IGif[];
}

interface IHistoryCommon {
  id: string;
  type: string;
  value: string;
}

export interface IHistoryUser extends IHistoryCommon {
  account: IUser;
}

export interface IHistoryNiche extends IHistoryCommon {
  niche: {
    gifs: number;
    id: string;
    name: string;
    previews: INichePreview[];
    subscribers: number;
    thumbnail: string;
  };
}

export interface IHistoryTag extends IHistoryCommon {
  tag: {
    count: number;
    name: string;
  };
}

export interface IHistoryFreeType extends IHistoryCommon {}

export type THistoryList = (IHistoryUser | IHistoryNiche | IHistoryTag | IHistoryFreeType)[];

export interface INiche {
  rules?: string | null;
  cover: string | null;
  following?: boolean;
  gifs: number;
  id: string;
  name: string;
  owner?: string;
  previews?: INichePreview[];
  subscribers: number;
  thumbnail: string | null;
  trending?: boolean;
  description: string;
  added?: boolean;
  available?: boolean;
  tags?: string[] | null;
  preferences?: string[] | null;
  banner?: string | null;
}

export interface IPaginatedNicheList {
  niches: INiche[];
  page: number;
  pages: number;
  total: number;
}

export interface INicheMap {
  [key: string]: INiche;
}

interface INichePreview {
  id: string;
  thumbnail: string;
}

export interface INichesItems {
  type: string;
  text: string;
  id: string;
  image: string | null;
  subscribers: number;
}

export interface IAddToHistory {
  value: string;
  type: string;
}

export interface ITag {
    name: string;
    count: number;
}

export interface ITagList {
  tags: ITag[];
}

export interface ITagsList {
  type: string;
  text: string;
  gifs: number;
}

export interface ISessionInfo {
  addr: string;
  agent: string;
  rtfm: string;
  token: string;
  session: string;
}

export interface IUserSettings {
  dismiss_scroll_more?: boolean;
  dismiss_niche_feedback_tooltip?: boolean;
}

interface IPremiumSubscription {
  subscription_outbound_link?: string;
}

export interface IUser {
    creationtime: number;
    description: string;
    followers: number;
    following: number;
    gifs: number;
    name: string;
    preferences?: string[]|null;
    profileImageUrl: string | null;
    profileUrl: string;
    publishedCollections: number;
    publishedGifs: number;
    roles?: string[];
    settings: IUserSettings;
    socialUrl1?: string; // pornhub
    socialUrl2?: string; // twitter
    socialUrl3?: string; // instagram
    socialUrl4?: string; // manyvids
    socialUrl5?: string; // reddit
    socialUrl6?: string; // onlyfans
    socialUrl7?: string; // patreon
    socialUrl8?: string; // xhamster
    socialUrl9?: string; // chaturbate
    socialUrl10?: string; // myfreecams
    socialUrl11?: string; // fansly
    socialUrl12?: string; // camsoda
    socialUrl13?: string; // fancentro
    socialUrl14?: string; // youtube
    socialUrl15?: string; // facebook
    socialUrl16?: string; // discord
    socialUrl17?: string; // clips4sale
    status: string;
    subscription: number;
    url: string;
    username: string;
    verified: boolean;
    ageVerified: boolean;
    views: number;
    email: string;
    studio?: boolean;
    blockedTags?: string[]|null;
    totalGifs?: number;
    premium?: IPremiumSubscription;
}

export interface IAuthClient {
    name: string;
    scopes: string[];
}

export interface IShortUser {
  creationtime: number;
  description: string | null;
  followers: number;
  gifs: number;
  name: string | null;
  profileImageUrl: string | null;
  username: string;
  verified: boolean;
  views: number;
  studio?: boolean;
}

export interface IShortUserList {
  creators: IShortUser[];
}

export interface IUserMap {
  [username: string]: IUser;
}

export interface IUserSearchResults {
  items: IUser[];
  page: number;
  pages: number;
  total: number;
}

export interface IMeGifsResponse {
  gifs: IGif[];
  page: number;
  pages: number;
  total: number;
  tags: string[];
  niches: string[];
  users: IUser[];
}

interface IBulkSubmitIdMap {
  [ticket: string]: string;
}

export interface IBulkSubmitResponse {
  gifs: string[];
  tickets: IBulkSubmitIdMap;
}

export interface ISubmitParams {
  ticketID: string | null;
  isPrivate: boolean;
  keepAudio: boolean;
  tags: string[];
  niches: string[];
  sexuality: string;
  startTime: number;
  description: string | null;
  endTime: number;
}

export interface ISubmitResponse {
  id: string;
  duration: number;
}

interface IUploadBatchResponseItem {
  id: string;
  url: string;
}

export interface IUploadBatchResponse {
  method: string;
  tickets: IUploadBatchResponseItem[];
}

export type UploadTicketType = "gif" | "verification" | "profile" | "niche";

export interface IUploadTicketParams {
  md5?: string | null;
  url?: string | null;
  type: UploadTicketType;
}

export interface IUploadTicketBatchParams {
  count: number;
  type: UploadTicketType;
}

export interface IUploadTicketResponse {
  id: string;
  status: string | null;
  kind: string;
  type: string | null;
  url?: string;
  method?: string;
  error?: string|null;
  downloadUrl?: string;
  timelineUrl?: string;
}

export interface ICreateGalleryData {
  files: string[];
  publish: boolean;
  tags: string[];
  niches: string[];
  preferences: string;
}

export interface ICreateGalleryResponse {
  id: string;
}

export interface IGalleryStatusResponse {
  id: string;
  status: string;
  error: string | null;
}

export interface IUploadStatusResponse {
  task: string;
  gifname?: string;
  id?: string;
  time?: number;
  progress?: number;
  createdDate?: number;
  status?: string;
}

export interface IErrorData {
  error?: {
    code: string;
    message: string;
    description?: string;
    delay?: number;
    status: number;
    niche_id?: string;
    public?: boolean;
    target?: string;
    gif?: string;
    subject?: string;
  };
}

export interface ICreateCollection {
  published: boolean;
  description: string | null;
  folderName: string;
}

export interface IAnalyticsDataset {
  current: number[];
  previous: number[];
}

export interface IAnalyticsGifInfo {
  id: string;
  views: number;
  likes: number;
  image: string;
  tags: string[];
}

export interface IAnalyticsSocialInfo extends IAnalyticsDataset {
  name: string;
  link: string;
}

export interface IAnalyticsResponse {
  datapoints: number;
  contentViews: IAnalyticsDataset;
  profileViews: IAnalyticsDataset;
  likes: IAnalyticsDataset;
  followers: IAnalyticsDataset;
  gifs: IAnalyticsGifInfo[];
  social: IAnalyticsSocialInfo[];
}

export interface INicheFeedbackRequest {
  niche: string;
  gif: string;
  state: string;
}

export interface INicheFeedbackResponse {
  gif: string;
  state: string | null;
  upvotes: number;
  downvotes: number;
  userName: string;
  timestamp: number;
}

export interface IAdSlotsResponse {
  slots: string[];
}

export interface IAdSlot {
  name: string;
  enabled: boolean;
}

export interface IAdManageSlotsResponse {
  slots: IAdSlot[];
}

export enum InfoBarTextThemeEnum {
  light = "light",
  dark = "dark",
}

export enum InfoBarBackgroundThemeEnum {
  halloween = "halloween",
  valentine = "valentines",
  blackFriday = "black_friday",
  christmas = "christmas",
  standard = "standard",
}

export interface IGlobalNotification {
  id: string;
  text: string;
  cta_link: string;
  cta_text: string;
  type: string;
  background_theme: InfoBarBackgroundThemeEnum;
  text_theme: InfoBarTextThemeEnum;
  close_button_enabled: number;
}

export interface IGlobalNotifications {
  global: IGlobalNotification[];
}

export interface IAliasLink {
  gif: string;
  username: string;
  id: string;
}

export interface IGetRelatedNicheParams {
  niche: string;
}

export interface ITagSuggestedOptions {
  count: string;
  query: string;
}

export interface IStreamateCameraRequest {
  exclude: string[];
}

export interface IStreamateCamera {
  id: string;
  kwikyId: string;
  nickname: string;
  preview: string;
  thumbnail: string;
  stream: string;
  url: string;
}

export interface ICreatorTagsResponse {
  tags: {
    all: {
      [key: string]: number;
    };
    gifs: {
      [key: string]: number;
    };
    images: {
      [key: string]: number;
    };
  };
}

export interface VerifyResponse {
  status: string | null;
}

interface ITagFeedback {
  tag_id: string;
  enabled: boolean;
}

export interface ITagFeedbackPayload {
  page: string;
  tags: ITagFeedback[];
}

export enum socialType {
  Twitter = "twitter",
  Pornhub = "pornhub",
  Instagram = "instagram",
  Manyvids = "manyvids",
  Reddit = "reddit",
  Onlyfans = "onlyfans",
  Patreon = "patreon",
  Xhamster = "xhamster",
  Chaturbate = "chaturbate",
  Myfreecams = "myfreecams",
  Fansly = "fansly",
  Camsoda = "camsoda",
  Fancentro = "fancentro",
  Youtube = "youtube",
  Discord = "discord",
  Facebook = "facebook",
  Clips4Sale = "clips4sale",
}

export interface ITagLandingFeed {
    tag: string;
    description?: string;
    relatedTags: string[];
    bestNiches: INiche[];
    relatedCreators: IUser[];
    bestGifs: IGif[];
    bestImages: IGif[];
    users: IUser[];
    niches: INiche[];
}

export interface ILocationState {
  [key: string]: string;
}

export interface IGeolocation {
  blocked: boolean;
  country: string | null;
  state: string | null;
}

export enum GifType {
  Image = 2,
  Video = 1
}

interface AdImage {
  url: string;
  w: number;
  h: number;
}

interface AdExtra {
  id: number;
  name: string;
  imgUrl: string;
  videoUrl: string | null;
  description: string;
  username: string;
  profile_name: string;
  landingpage: string;
  freetrial: boolean;
  location: string;
  favs: string;
  price: string;
  gender: string;
  customModel: boolean;
  photosCount: string;
  subscribersCount: string;
  likeCount: string;
  postsCount: string;
  videosCount: string;
}

interface IAdsCreator {
  link: string;
  image: AdImage;
  extra: AdExtra;
}

export type OnlyFansSource = "sidebar" | "module";

export interface IAdsCreators {
  source: OnlyFansSource;
  ads: IAdsCreator[];
}

export type TAddFeedback = (params: Omit<INicheFeedbackRequest, "niche">) => void;

export interface INichesCategories {
  categories: string[];
}

export enum FeedTypeEnum {
  FOR_YOU = "for-you",
  TRENDING = "trending",
  WATCH = "watch",
  NICHE = "niche",
  TAG_GIFS = "tag-gifs",
  TAG_IMAGES = "tag-images",
  SEARCH_GIFS = "search-gifs",
  SEARCH_IMAGES = "search-images",
  EXPLORE_GIFS = "explore-gifs",
  EXPLORE_IMAGES = "explore-images",
  PROFILE = "profile",
  COLLECTION = "collection",
  FAVOURITES = "favourites",
  SFW = "sfw",
}

export enum FeedModuleTypeEnum {
  BOOST = "boost",
  SUGGESTED_NICHES = "suggested-niches",
  SECONDARY_SUGGESTED_NICHES = "secondary-suggested-niches",
  SUGGESTED_CREATORS = "suggested-creators",
  TRENDING_CREATORS = "trending-creators",
  TRENDING_NICHES = "trending-niches",
  TRENDING_TAGS = "trending-tags",
  NICHE_EXPLORER = "niche-explorer",
  RELATED_TAGS = "related-tags",
  LIVE_CAM = "live-cam",
  ONLY_FANS = "only-fans",
  PAID_LINKS = "paid-links",
}

export interface IFeedModule {
  type: FeedModuleTypeEnum;
  position: number;
  position_factor: number;
}

export type IFeedModules = {
  [key in FeedTypeEnum]: IFeedModule[];
}

export interface IFeedModulesResponse {
  modules: IFeedModules;
}

export type SearchGifsOrder = "latest" | "trending" | "top7" | "top28" | "score";

export interface IAgeGateSettingsResponse {
  redirect_url: string;
}

interface IPaidLink {
  image: string;
  title: string;
  button_text: string;
  button_link: string;
  username: string;
}

export interface IPaidLinks {
  links: IPaidLink[];
}
